import { getMediaFilesByFileId, postMediaFilesBatch, postMediaFilesUploadUrl, postMediaFilesRegister } from '../api/familypal/sdk.gen';
import type { MediaFile } from './types';

// Upload a file to a presigned URL with progress tracking via XHR.
// On mobile, use UploadManager instead for native streaming (no OOM on large videos).
function uploadWithProgress(
  fileUri: string,
  uploadUrl: string,
  mimeType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      const blob = await fetch(fileUri).then((r) => r.blob());
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.ontimeout = () => reject(new Error('Upload timed out'));
      xhr.timeout = 900000; // 15 minutes to match presigned URL expiry

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', mimeType || 'application/octet-stream');
      xhr.send(blob);
    } catch (err) {
      reject(err);
    }
  });
}

function normalizeMediaFile(f: any): MediaFile {
  return {
    id: f.id,
    type: f.type || '',
    uri: f.uri || '',
    bucket: f.bucket || '',
    key: f.key || '',
    name: f.name || '',
    size: typeof f.size === 'string' ? parseInt(f.size, 10) : (f.size || 0),
    mimeType: f.mime_type || f.mimeType || '',
    thumbnail: f.thumbnail || '',
    thumbnailUri: f.thumbnail_uri || f.thumbnailUri || '',
    width: f.width || 0,
    height: f.height || 0,
    miniPreview: f.mini_preview || f.miniPreview || '',
    hasPreview: f.has_preview ?? f.hasPreview ?? false,
  };
}

export async function getMediaFiles(fileIds: string[]): Promise<{ files: MediaFile[] }> {
  if (fileIds.length === 0) return { files: [] };

  // Try batch endpoint first, fall back to individual fetches
  try {
    const batchResponse = await postMediaFilesBatch({
      body: { file_ids: fileIds },
    } as any);
    if (batchResponse.data) {
      const batchData = batchResponse.data as any;
      const rawFiles = batchData.files || batchData;
      if (Array.isArray(rawFiles) && rawFiles.length > 0) {
        return { files: rawFiles.map(normalizeMediaFile) };
      }
    }
  } catch (err) {
    console.warn('Batch file fetch failed, falling back to individual:', err);
  }

  // Fallback: fetch files individually
  const results = await Promise.all(
    fileIds.map(async (fileId) => {
      try {
        const response = await getMediaFilesByFileId({ path: { file_id: fileId } } as any);
        if (response.error || !response.data) {
          console.warn(`Failed to fetch file ${fileId}:`, response.error);
          return null;
        }
        return response.data;
      } catch (err) {
        console.warn(`Failed to fetch file ${fileId}:`, err);
        return null;
      }
    })
  );

  const files = results.filter((f): f is NonNullable<typeof f> => f !== null).map(normalizeMediaFile);
  return { files };
}

export async function uploadMediaFile(
  file: { uri: string; name: string; mimeType: string; size: number },
  dataId: string,
  onProgress?: (progress: number) => void,
): Promise<MediaFile> {
  // Determine file type from MIME type
  let fileType = 'FILE_TYPE_FILE';
  if (file.mimeType?.startsWith('image/')) {
    fileType = 'FILE_TYPE_IMAGE';
  } else if (file.mimeType?.startsWith('video/')) {
    fileType = 'FILE_TYPE_VIDEO';
  } else if (file.mimeType?.startsWith('audio/')) {
    fileType = 'FILE_TYPE_AUDIO';
  }

  // Step 1: Get presigned URL
  const { data: uploadInfo } = await postMediaFilesUploadUrl({
    body: { filename: file.name, type: fileType, mime_type: file.mimeType, file_size: file.size },
    throwOnError: true,
  } as any);
  const info = uploadInfo as any;

  onProgress?.(5);

  // Step 2: Upload to presigned URL with progress tracking
  const uploadUrl = info.upload_url || info.uploadUrl;
  await uploadWithProgress(file.uri, uploadUrl, file.mimeType, (pct) => {
    // Map upload progress to 5-90% of overall progress
    onProgress?.(5 + Math.round(pct * 0.85));
  });

  onProgress?.(90);

  // Step 3: Register the upload
  const fileId = info.file_id || info.fileId;
  const { data: registered } = await postMediaFilesRegister({
    body: {
      file_id: fileId,
      filename: file.name,
      type: fileType,
      mime_type: file.mimeType,
      file_size: file.size,
      data_id: dataId,
    },
    throwOnError: true,
  } as any);

  onProgress?.(100);

  return normalizeMediaFile(registered);
}

export async function uploadFamilyFile(
  file: { uri: string; name: string; mimeType: string; size: number },
  familyId: string,
  onProgress?: (progress: number) => void,
): Promise<MediaFile> {
  let fileType = 'FILE_TYPE_FILE';
  if (file.mimeType?.startsWith('image/')) {
    fileType = 'FILE_TYPE_IMAGE';
  } else if (file.mimeType?.startsWith('video/')) {
    fileType = 'FILE_TYPE_VIDEO';
  } else if (file.mimeType?.startsWith('audio/')) {
    fileType = 'FILE_TYPE_AUDIO';
  }

  const { data: uploadInfo } = await postMediaFilesUploadUrl({
    body: { filename: file.name, type: fileType, mime_type: file.mimeType, file_size: file.size },
    throwOnError: true,
  } as any);
  const info = uploadInfo as any;

  onProgress?.(5);

  const uploadUrl = info.upload_url || info.uploadUrl;
  await uploadWithProgress(file.uri, uploadUrl, file.mimeType, (pct) => {
    onProgress?.(5 + Math.round(pct * 0.85));
  });

  onProgress?.(90);

  const fileId = info.file_id || info.fileId;
  const { data: registered } = await postMediaFilesRegister({
    body: {
      file_id: fileId,
      filename: file.name,
      type: fileType,
      mime_type: file.mimeType,
      file_size: file.size,
      family_id: familyId,
      owner_type: 'OWNER_TYPE_FAMILY',
    },
    throwOnError: true,
  } as any);

  onProgress?.(100);

  return normalizeMediaFile(registered);
}

export async function uploadProfilePhoto(
  file: { uri: string; name: string; mimeType: string; size: number },
  personId: string,
  onProgress?: (progress: number) => void,
): Promise<MediaFile> {
  // Determine file type from MIME type
  let fileType = 'FILE_TYPE_FILE';
  if (file.mimeType?.startsWith('image/')) {
    fileType = 'FILE_TYPE_IMAGE';
  } else if (file.mimeType?.startsWith('video/')) {
    fileType = 'FILE_TYPE_VIDEO';
  } else if (file.mimeType?.startsWith('audio/')) {
    fileType = 'FILE_TYPE_AUDIO';
  }

  // Step 1: Get presigned URL (entity-agnostic)
  console.error('[uploadProfilePhoto] Step 1: Getting presigned URL...');
  const { data: uploadInfo } = await postMediaFilesUploadUrl({
    body: { filename: file.name, type: fileType, mime_type: file.mimeType, file_size: file.size },
    throwOnError: true,
  } as any);
  const info = uploadInfo as any;
  console.error('[uploadProfilePhoto] Step 1 done. file_id:', info?.file_id || info?.fileId);

  onProgress?.(5);

  // Step 2: Upload to presigned URL with progress
  const uploadUrl = info.upload_url || info.uploadUrl;
  await uploadWithProgress(file.uri, uploadUrl, file.mimeType, (pct) => {
    onProgress?.(5 + Math.round(pct * 0.85));
  });

  onProgress?.(90);

  // Step 3: Register the upload with person_id and owner_type
  console.error('[uploadProfilePhoto] Step 3: Registering upload...');
  const fileId = info.file_id || info.fileId;
  const { data: registered } = await postMediaFilesRegister({
    body: {
      file_id: fileId,
      filename: file.name,
      type: fileType,
      mime_type: file.mimeType,
      file_size: file.size,
      person_id: personId,
      owner_type: 'OWNER_TYPE_PERSON',
    },
    throwOnError: true,
  } as any);

  onProgress?.(100);

  return normalizeMediaFile(registered);
}
